import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useViewportProvider from '@/hooks/useViewportProvider';
import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { fetchOpenedFile } from '@/redux/reducers/datasetSlice';
import { showModal } from '@/redux/reducers/modalsSlice';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import {
  applyFormula,
  setFormulaToEdit,
  setInsertNewColumnNextTo,
  setIsInsertingNewColumn,
  setNewColumnName,
  setResetFormulaForm,
} from '@/redux/reducers/spreadsheetSlice';

import ShowToast from '@/Components/Toast/showToastTemplate';
import { Button } from '@/Components/UI/Button/Button';
import Textarea from '@/Components/UI/Form/Textarea/Textarea';
import TextInput from '@/Components/UI/Form/TextInput/TextInput';
import { hasNoWritePermission } from '@/helpers/spreadsheetHelper';
import useOnClickOutside from '@/hooks/useClickOutside';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { get, post } from '@/Utils/API';
import {
  DOCS,
  FORMULA,
  FORMULAS,
  FUNCTIONS,
  GENERAL_ERROR_TEXT,
  ITERATOR_COLUMN,
  LINK_HOW_TO_USE_FORMULAS,
  MODAL_CREATE_ACCOUNT,
  MODAL_MAKE_COPY,
  TOAST_TEXT_APPLY_FUNCTION_ERROR,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  URL_DATASET,
  VALIDATE,
} from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { throwNewErrorWithMessage } from '@/Utils/utils';
import CustomToggleSwitch from '../CustomToggleSwitch/CustomToggleSwitch';
import Label from '../UI/Form/Label/Label';
import { SelectWithoutPopover } from '../UI/Form/Select/Select-Without-Popover';
import Icon from '../UI/Icon/Icon';
import Spinner from '../UI/Spinner/Spinner';
import Tooltip from '../UI/Tooltip/Tooltip';
import AIFormulaTextarea from './AIFormulaTextarea';
import ColumnSelector from './ColumnSelector';
import FormulaPreviewContainer from './FormulaPreviewContainer';
import {
  columnNameRegex,
  defaultOptions,
  findActiveFunction,
  getAutocompleteList,
  getLastVisibleColumn,
  getLastWord,
  regexFnNoClosingBraceGlobal,
} from './helpers';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

// The regular expression /\s+/g matches one or more consecutive whitespace characters in a string.
const cleanSpacesRegex = /\s+/g;
//  The expression /^\s+/gm will remove any leading spaces at the beginning of each line in the text.
const trimStartRegex = /^\s+/gm;
// This regular expression checks whether a string starts or ends with a non-alphanumeric, non-numeric, and non-underscore character.
const symbolsRegex = /^[\W]|[\W]$/;
// This regular expression finds all occurrences of the "<" character in the text.
const lessThanGlobalRegex = /</g;
// This regular expression finds all occurrences of the ">" character in the text.
const moreThanGlobalRegex = />/g;

const FormulasPopover = ({ close: hideModal, open: show, agGrid }) => {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const currentFile = useCurrentFile();
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const { user } = useSelector((state) => state[userDataSliceName]);
  const {
    columnDefs,
    newColumnMode,
    formulaToEdit,
    newColumnName: columnName,
    clientState: { sortModel, filterModel },
    insertNewColumnNextTo,
    resetFormulaForm,
  } = useSelector((store) => store[spreadsheetSliceName]);
  const uuid = getFileUuidFromPath();

  const [currentModalOption, setCurrentModalOption] = useState('formula');
  const [formula, setFormula] = useState('');
  const [convertedFormula, setConvertedFormula] = useState('');
  const [requestFormula, setRequestFormula] = useState('');
  const [functionExamples, setFunctionExamples] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [usedFunctionExamples, setUsedFunctionExamples] = useState([]);
  const [duplicatedHeaderNameHelperText, setDuplicatedHeaderNameHelperText] = useState('');
  const [isValidFormula, setIsValidFormula] = useState(false);
  const [isFormulasInputFocused, setIsFormulasInputFocused] = useState(false);
  const [textAreaTopScroll, setTextAreaTopScroll] = useState(0);
  const [textAreaScrollbarWidth, setTextAreaScrollbarWidth] = useState(0);
  const [currentAutocompleteValue, setCurrentAutocompleteValue] = useState('');
  const [autocompleteList, setAutocompleteList] = useState([]);
  const [isAutocompleteBoxFocused, setIsAutocompleteBoxFocused] = useState(false);
  const [isAIFormulaMode, setIsAIFormulaMode] = useState(true);
  const [formulaPreview, setFormulaPreview] = useState([]);
  const [showFormulaBuilderFeedback, setShowFormulaBuilderFeedback] = useState(false);
  const [aiFormulaQuery, setAiFormulaQuery] = useState('');
  const [aiFormulaResponse, setAiFormulaResponse] = useState('');

  const { metadata } = currentFile || {};
  const isSnowflakeFile = metadata?.Details?.type?.includes('snowflake');

  const textareaRef = useRef(null);
  const fakeTextareaRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isNarrowScreen } = useViewportProvider();
  const textareaHeight = isNarrowScreen ? 'h-11' : 'h-28';
  const defaultOptionsOfFunctions = useMemo(
    () => Object.keys(functionExamples).map((fn) => fn.toUpperCase()),
    [functionExamples]
  );

  const maxAutocompleteListLength = 25;

  useOnClickOutside(autocompleteRef, () => setIsAutocompleteBoxFocused(false));

  const setColumnName = (name) => {
    dispatch(setNewColumnName(name));
  };

  const sortedDefaultOptionsOfFunctions = useMemo(
    () =>
      [...defaultOptionsOfFunctions].sort((a, b) => {
        return b.length - a.length;
      }),
    [defaultOptionsOfFunctions]
  );

  const _columnDefs = useMemo(
    () =>
      columnDefs.map((el, indx) => {
        let columnName = el.headerName;

        if (Number(columnName)) {
          columnName = `{${columnName}}`;
        } else {
          const isColumnNameIncludesFormula = defaultOptionsOfFunctions.some((fn) =>
            regexFnNoClosingBraceGlobal(fn).test(columnName)
          );

          if (isColumnNameIncludesFormula) {
            columnName = `{${columnName}}`;
          }

          if (!isColumnNameIncludesFormula) {
            const isColumnNameIncludesColumnName = columnDefs.some(({ headerName }, i) => {
              if (i !== indx) {
                if (columnNameRegex(headerName).test(el.headerName)) {
                  return true;
                }
              }
            });

            if (isColumnNameIncludesColumnName) {
              columnName = `{${columnName}}`;
            }
          }
        }

        return {
          ...el,
          headerName: columnName.trim().replace(cleanSpacesRegex, ' '),
          labelName: el.headerName.replace(cleanSpacesRegex, ' '),
        };
      }),
    [columnDefs, defaultOptionsOfFunctions]
  );

  const sortedColumnDefs = useMemo(
    () =>
      [..._columnDefs].sort((a, b) => {
        return b.headerName.length - a.headerName.length;
      }),
    [_columnDefs]
  );

  const listOfDuplicatedHeaderNames = useMemo(
    () =>
      _columnDefs
        .map(({ headerName }) => headerName)
        .reduce((acc, headerName, _, arr) => {
          if (arr.indexOf(headerName) !== arr.lastIndexOf(headerName)) {
            return [...acc, headerName];
          }
          return acc;
        }, []),
    [_columnDefs]
  );

  const columnOptions = useMemo(
    () =>
      _columnDefs.map(({ headerName, fieldType: type, labelName }) => {
        return { value: headerName, label: labelName, type };
      }),
    [_columnDefs]
  );

  const functionOptions = defaultOptionsOfFunctions.map((fn) => {
    return { value: fn + '()', label: fn + '()' };
  });

  const lastNumberOfDefaultColumnName = useMemo(
    () =>
      _columnDefs.reduce((acc, { headerName }) => {
        if (headerName.startsWith('Column')) {
          const value = headerName.replace('Column', '');
          if (value && Number(value.trim()) && Number(value.trim()) > acc) {
            return +value;
          }
        }
        return acc;
      }, 0),
    [_columnDefs]
  );

  const getTextareaPlaceholder = () => {
    return isNarrowScreen
      ? 'CONCAT(Col_Text, “Sample text”)'
      : 'CONCAT(Col_Text, “Sample text”)\n(Col_Number * 3) + 65';
  };

  const isValidFormulaRequest = async () => {
    if (currentModalOption === 'formula') {
      const body = {
        formula: requestFormula,
      };
      try {
        const response = await post(`${URL_DATASET}/${uuid}/${FORMULA}/${VALIDATE}`, body);

        if ('Success' in response && !response.Success) {
          setIsValidFormula(false);
        } else {
          const { isValid } = response;
          setIsValidFormula(isValid && 'valid');
        }
      } catch (error) {
        setIsValidFormula(false);
        throw new Error(error);
      }
    }
  };

  const findFunctionName = (string) => {
    const escapedMarkup = string
      .replace(lessThanGlobalRegex, '&lt;')
      .replace(moreThanGlobalRegex, '&gt;');

    const _string = sortedDefaultOptionsOfFunctions.reduce((acc, fn) => {
      const newString = acc.replaceAll(regexFnNoClosingBraceGlobal(fn), (value) => {
        const _value = value.replace('(', '');
        return `<span data-cy='formula-function-${_value}' class="!text-carrot-800">${_value}</span>(`;
      });

      return newString;
    }, escapedMarkup);

    return _string;
  };

  const findColumnName = (string) => {
    const _string = sortedColumnDefs.reduce((acc, { headerName }) => {
      const headerNameWithoutMarkup = headerName
        .replaceAll(lessThanGlobalRegex, '&lt;')
        .replace(moreThanGlobalRegex, '&gt;');

      if (!Number(headerName) && headerName.trim()) {
        const isWithSpecificSymbols = symbolsRegex.test(headerNameWithoutMarkup);

        const newString = acc.replaceAll(
          isWithSpecificSymbols
            ? headerNameWithoutMarkup
            : columnNameRegex(headerNameWithoutMarkup),
          `<span class="text-midnight-400">${headerNameWithoutMarkup}</span>`
        );
        return newString;
      }
      return acc;
    }, string);

    setConvertedFormula(_string);
    return _string;
  };

  const defineColumnId = (string) => {
    const _string = sortedColumnDefs.reduce((acc, { headerName, colId }) => {
      if (!Number(headerName) && headerName.trim()) {
        const isWithSpecificSymbols = symbolsRegex.test(headerName);

        const newString = acc.replaceAll(
          isWithSpecificSymbols ? headerName : columnNameRegex(headerName),
          `<span>${colId}</span>`
        );
        return newString;
      }
      return acc;
    }, string);

    return _string.replaceAll('<span>', '').replaceAll('</span>', '');
  };

  const getFunctionsExamples = async () => {
    setIsLoading(true);
    const endpoint = `${DOCS}/${FORMULAS}/${FUNCTIONS}${
      isSnowflakeFile ? '?dialect=snowflake' : ''
    }`;
    try {
      const response = await get(endpoint);
      setFunctionExamples(response);
    } catch (error) {
      hideModal();
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
        errorContext: error,
        endpoint: endpoint,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const defineColumnName = () => {
    if (currentModalOption === 'formula') {
      return columnName?.trim() === '' ? formula : columnName;
    } else {
      return columnName?.trim() === '' ? `Column ${lastNumberOfDefaultColumnName + 1}` : columnName;
    }
  };

  const handleApplyFormula = () => {
    const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
    const toastErrorText =
      'An error occurred while applying the formula. Please try again later or contact support.';
    const showErrorToast = (errorContext) => {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: errorContext?.message || toastErrorText,
        errorContext: errorContext,
        fileAndFunction: fileAndFunction,
      });
    };

    try {
      setIsLoading(true);
      dispatch(
        applyFormula({
          requestFormula,
          columnName: defineColumnName(),
          formulaToEdit,
          insertNewColumnNextTo,
          agGrid,
          currentModalOption,
          fileAndFunction,
          toastErrorText,
        })
      )
        .then(() => {
          setIsLoading(false);
          hideModal();
        })
        .catch((error) => {
          setIsLoading(false);
          showErrorToast(error);
        });
    } catch (error) {
      showErrorToast(error);
    }
  };

  const onAddIteratorColumn = async () => {
    if (isPreviewMode) {
      hideModal();
      return showPreviewModeUpsellModal();
    }
    dispatch(setIsInsertingNewColumn(true));
    setIsLoading(true);
    try {
      const payload = {
        filterModel,
        sortModel,
        columnName: defineColumnName(),
      };

      const lastVisibleColumn = currentModalOption === 'blank' && getLastVisibleColumn(agGrid);

      if (insertNewColumnNextTo || lastVisibleColumn) {
        payload.afterColumnId = insertNewColumnNextTo || lastVisibleColumn;
      }
      const endpoint = `${URL_DATASET}/${uuid}/${ITERATOR_COLUMN}`;
      const result = await post(endpoint, payload);

      if (!result.Success) {
        ShowToast({
          type: TOAST_TYPE_ERROR,
          text: result?.Message || TOAST_TEXT_APPLY_FUNCTION_ERROR,
          errorContext: result,
          endpoint: endpoint,
          payload: payload,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
      } else {
        await dispatch(fetchOpenedFile(uuid));
        dispatch(
          addMixpanelEvent({
            eventName: 'Function Used',
            eventProps: { FunctionName: 'Iterator Column' },
            userIncrementName: '# of function use',
          })
        );
      }
    } catch (e) {
      throw new Error(e);
    } finally {
      dispatch(setIsInsertingNewColumn(false));
      setIsLoading(false);
      hideModal();
    }
  };

  const updateFormula = (newValue) => {
    const _newValue = newValue.replace(cleanSpacesRegex, ' ').replace(trimStartRegex, '');
    setFormula(_newValue);
  };

  const onAddFormulaOrColumn = (value, extraIndex = 0) => {
    if (value) {
      const activeCaretIndex = textareaRef?.current?.selectionStart;
      setFormula((prev) => {
        const charsBeforeIndex = prev.slice(0, activeCaretIndex - extraIndex);
        const charsAfterIndex = prev.slice(activeCaretIndex);
        return charsBeforeIndex + value + charsAfterIndex;
      });
    }
  };

  const closeModal = () => {
    dispatch(setFormulaToEdit(null));
    setColumnName('');
    hideModal();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || (e.key === 'Enter' && e.shiftKey)) {
      e.preventDefault();
    }
  };

  const defaultTooltipContent = () => {
    return (
      <div className='flex flex-col text-[10px] !font-extrabold'>
        <span className='text-midnight-100 !text-left uppercase'>examples</span>
        <ul className='text-midnight-100 !text-left'>
          <li className='text-white !text-left !font-normal'>{'Quantity*14'}</li>
          <li className='text-white !text-left !font-normal'>{'CONCAT("First Name: ", Name)'}</li>
          <li className='text-white !text-left !font-normal'>{'IF(Amount > 5, "Big", "Small")'}</li>
        </ul>
      </div>
    );
  };

  const tooltipContent = () => {
    return usedFunctionExamples?.map((fn, i) => {
      return (
        <div key={i} className='flex flex-col text-[10px] !font-extrabold'>
          <span className='text-midnight-100 !text-left'>{functionExamples[fn]?.title}</span>
          <span className='text-white !text-left !font-normal'>
            {functionExamples[fn]?.documentation}
          </span>
          <ul className='text-midnight-100 !text-left'>
            {functionExamples[fn]?.examples.map(({ formula, output }, i) => {
              return (
                <li key={i} className='flex flex-row'>
                  <span className='text-white !font-normal'>EX:</span>
                  {` ${formula} => ${output}`}
                </li>
              );
            })}
          </ul>
        </div>
      );
    });
  };

  const onHelpButtonClick = () => {
    window.open(LINK_HOW_TO_USE_FORMULAS, '_blank', 'noopener,noreferrer');
  };

  const isAddButtonDisabled = () => {
    if (currentModalOption === 'blank') return isLoading;
    if (currentModalOption === 'formula') {
      return isLoading || isValidFormula !== 'valid';
    }
  };

  const textareaHelperText = () => {
    if (formula) {
      switch (isValidFormula) {
        case false: {
          return (
            <span
              data-cy='invalid-formula'
              className='text-violet-web-900 bg-violet-web-100 uppercase text-[10px] w-fit rounded-sm font-extrabold px-1'
            >
              Invalid formula
            </span>
          );
        }
        case 'pending': {
          return (
            <div className='animate-pulse text-ui-helper uppercase text-[10px] w-fit rounded-sm font-extrabold px-1'>
              <Spinner size='small' color='shadow' className='inline-block' />
              Validating formula
            </div>
          );
        }

        default:
          return '';
      }
    } else {
      return '';
    }
  };

  const setActiveFunction = () => {
    const activeCaretIndex = textareaRef?.current?.selectionStart;
    const leftPartOfFormula = formula.slice(0, activeCaretIndex);
    findActiveFunction(leftPartOfFormula, defaultOptionsOfFunctions, setUsedFunctionExamples);
  };

  const defineFakeTextareaPositioning = () => {
    setTextAreaTopScroll(-textareaRef?.current?.scrollTop);
    setTextAreaScrollbarWidth(
      textareaRef?.current?.offsetWidth - textareaRef?.current?.clientWidth + 7
    );
  };

  const onTextareaChange = (e) => {
    defineFakeTextareaPositioning();
    updateFormula(e.target.value);
  };

  const onTextareaFocus = () => {
    setIsFormulasInputFocused(true);
  };

  const onTextareaBlur = () => {
    setIsFormulasInputFocused(false);
  };

  const onTextareaKeyUp = () => {
    setActiveFunction();
    defineAutocompleteValue();
  };

  const onTextareaMouseUp = () => {
    setActiveFunction();
  };

  const handleCurrentModalOption = (v) => {
    setCurrentModalOption(v);
  };

  const onTextareaScroll = () => {
    defineFakeTextareaPositioning();
  };

  const tooltipContentCondition = usedFunctionExamples.length
    ? tooltipContent()
    : !usedFunctionExamples.length && defaultTooltipContent();

  const defineAutocompleteValue = () => {
    const activeCaretIndex = textareaRef?.current?.selectionStart;
    const autocompleteValue = getLastWord(formula, activeCaretIndex);
    setCurrentAutocompleteValue(autocompleteValue);
  };

  const onAutocompleteValueClick = (item) => {
    onAddFormulaOrColumn(item, currentAutocompleteValue.length);
    setCurrentAutocompleteValue('');
    textareaRef.current.focus();
  };

  const autocompleteContent = () => {
    const isAutoComplete =
      !!autocompleteList.length &&
      !!formula.trim() &&
      currentAutocompleteValue.length > 1 &&
      (isAutocompleteBoxFocused || isFormulasInputFocused);

    const getAutocompleteOption = (item) => {
      const maxCharacterNumber = item[1] === 'function' ? 20 : 25;
      const leftPartOfItem = item[0]
        .slice(0, currentAutocompleteValue?.length)
        .slice(0, maxCharacterNumber);
      const rightPartOfItem = item[0]
        .slice(currentAutocompleteValue?.length)
        .slice(0, maxCharacterNumber - leftPartOfItem.length);
      const isOverWidth = item[0].length > maxCharacterNumber;

      return (
        <>
          <span className='font-bold'>{leftPartOfItem}</span>
          <span className='font-normal'>{rightPartOfItem}</span>
          {isOverWidth && '...'}
        </>
      );
    };

    return (
      isAutoComplete && (
        <div
          ref={autocompleteRef}
          className='absolute mt-1 z-10 p-1 w-[295px] bg-white text-ui border-ui-200 border-2 max-h-36 !overflow-y-auto rounded-b'
        >
          <ul>
            {autocompleteList.map((item, i) => {
              const isFunctionItem = item[1] === 'function';
              return (
                <li
                  key={i}
                  className='leading-8 hover:bg-ocean-blue-100 hover:text-ui hover:rounded'
                >
                  <Button
                    className='!w-full !justify-start'
                    onClick={() => onAutocompleteValueClick(item[0])}
                    variant='ghost'
                    color='transparent'
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className='flex items-center justify-start min-w-[240px]'>
                      <div
                        className={clsx(
                          'flex grow normal-case text-sm',
                          isFunctionItem && 'text-carrot-800'
                        )}
                      >
                        {getAutocompleteOption(item)}
                      </div>
                      <span className='text-ui-helper font-extrabold grow-0 text-[10px]'>
                        {item[1]}
                      </span>
                    </div>
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )
    );
  };

  const onColumnNameChange = (e) => {
    setColumnName(e.target.value);
  };

  const handleAIFormulaToggle = () => {
    setIsAIFormulaMode((v) => !v);
  };

  const handleGenerateFormulaButtonClick = async (value) => {
    setIsLoading(true);
    const endpoint = `ai/${getFileUuidFromPath()}/formulate`;
    const payload = { Query: value };
    try {
      const response = await post(endpoint, payload);
      if ('Success' in response && !response.Success) {
        throwNewErrorWithMessage(response?.Message);
      } else {
        setFormula(response.Formula);
        setAiFormulaResponse(response.Formula);
        handleGeneratePreview(response.Formula);
      }
    } catch (error) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: error?.message || GENERAL_ERROR_TEXT,
        errorContext: error,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePreview = async (formulaToPreview = null) => {
    setIsLoading(true);
    const endpoint = `${URL_DATASET}/${getFileUuidFromPath()}/formula/preview`;
    const payload = {
      formula: formulaToPreview ? defineColumnId(formulaToPreview) : requestFormula,
    };
    try {
      if (!isSnowflakeFile) {
        const response = await post(endpoint, payload);
        if ('Success' in response && response.Success) {
          setFormulaPreview(response?.response_data);
          setShowFormulaBuilderFeedback(!!formulaToPreview);
        } else {
          throwNewErrorWithMessage(response?.Message);
        }
      }
    } catch (error) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: error?.message || GENERAL_ERROR_TEXT,
        errorContext: error,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDownGeneratePreview = (e) => {
    if (e?.key === 'Enter' && !isLoading && aiFormulaQuery) {
      e?.preventDefault(); // Prevents the default behavior of adding a new line
      handleGenerateFormulaButtonClick();
    }
  };

  useEffect(() => {
    if (isFormulasInputFocused) {
      setIsAutocompleteBoxFocused(true);
    }
  }, [isFormulasInputFocused]);

  useEffect(() => {
    if (currentAutocompleteValue) {
      const autocompleteList = getAutocompleteList({
        columns: _columnDefs,
        functions: defaultOptionsOfFunctions,
        currentAutocompleteValue,
        maxAutocompleteListLength,
      });

      setAutocompleteList(autocompleteList);
    }
  }, [currentAutocompleteValue]);

  useEffect(() => {
    if (listOfDuplicatedHeaderNames.length) {
      setDuplicatedHeaderNameHelperText(
        "This sheet has identical column names, and the formula will use the first matching column found, potentially impacting the formula's result. To avoid this, rename the identical columns."
      );
    }
  }, [listOfDuplicatedHeaderNames]);

  useEffect(() => {
    setCurrentModalOption(newColumnMode || 'formula');
  }, [newColumnMode]);

  useEffect(() => {
    if (formulaToEdit) {
      setIsAIFormulaMode(false); // when editing, by default, no AI mode
      updateFormula(formulaToEdit.formula.literal);
      setColumnName(formulaToEdit.columnName);
    }
  }, [formulaToEdit]);

  useEffect(() => {
    findColumnName(findFunctionName(formula));
    setRequestFormula(defineColumnId(formula));
    defineAutocompleteValue();
  }, [formula, functionExamples]);

  useEffect(() => {
    if (currentModalOption === 'formula') {
      setIsValidFormula('pending');
      const debounceTimer = setTimeout(() => {
        isValidFormulaRequest();
      }, 500);

      return () => {
        clearTimeout(debounceTimer);
      };
    }
  }, [formula, requestFormula, currentModalOption]);

  useEffect(() => {
    defineFakeTextareaPositioning();
    getFunctionsExamples();

    if (resetFormulaForm) {
      setFormula('');
      setColumnName('');
    }

    return () => {
      hideModal();
      dispatch(setInsertNewColumnNextTo(null));
      dispatch(setResetFormulaForm(false));
      dispatch(setFormulaToEdit(null));
      setColumnName('');
    };
  }, []);

  useEffect(() => {
    if (!user?.is_anonymous && currentFile?.isFileShared && hasNoWritePermission(currentFile)) {
      dispatch(showModal({ name: MODAL_MAKE_COPY }));
    }
  }, []);

  useEffect(() => {
    if (show) {
      setActiveFunction();
    }
  }, [convertedFormula, formula, show]);

  return (
    <div data-cy='formula-popover-body' className='relative'>
      {isLoading && (
        <div className='absolute top-0 bottom-0 left-0 right-0 z-50 flex items-center justify-center bg-white opacity-50 bg'>
          <Spinner size='medium' color='shadow' className='inline-block' />
        </div>
      )}
      <TextInput
        placeholder='New Column Name'
        className='w-full mt-2 mb-4 active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100'
        value={columnName}
        onChange={onColumnNameChange}
        dataCy='formula-new-column-name'
      />
      <SelectWithoutPopover
        options={defaultOptions}
        value={currentModalOption}
        onChange={(v) => handleCurrentModalOption(v)}
        color='oceanBlueLight'
        buttonClassName='active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100'
        dataCy='formula-mode-selector'
      />

      {duplicatedHeaderNameHelperText && (
        <div className='flex align-middle gap-2 my-2 uppercase text-capri-900 font-extrabold text-[10px] bg-capri-100 bg-opacity-40 p-2 rounded'>
          <Icon name='info' size='28' />
          <div>{duplicatedHeaderNameHelperText}</div>
        </div>
      )}

      {currentModalOption === 'formula' && (
        <div className='flex flex-col my-1'>
          <div className='flex items-center justify-between my-2'>
            <Label dataCy={'formula-label'} text={'Formula'} />
            <div className='flex items-center justify-start gap-2'>
              <Icon
                name='sparkle'
                size={16}
                weight={isAIFormulaMode ? 'duotone' : 'regular'}
                color={isAIFormulaMode ? '#3126AF' : ''}
              />
              <Label dataCy={'formula-label'} text={'Use AI'} className={clsx('leading-none')} />
              <CustomToggleSwitch
                cypressId='formula-ai-toggle'
                onChange={handleAIFormulaToggle}
                isChecked={isAIFormulaMode}
                switcher='SimpleSwitcher'
              />
            </div>
          </div>
          {isAIFormulaMode && (
            <AIFormulaTextarea
              isLoading={isLoading}
              handleGenerateFormulaButtonClick={handleGenerateFormulaButtonClick}
              handleKeyDownGeneratePreview={handleKeyDownGeneratePreview}
              aiFormulaQuery={aiFormulaQuery}
              setAiFormulaQuery={setAiFormulaQuery}
            />
          )}
          <div
            className={clsx(
              'flex justify-start items-center gap-2 p-2 bg-ui-secondary rounded-t border  border-ui-200 border-b-0 w-full',
              isAIFormulaMode && !formula && 'hidden'
            )}
          >
            <ColumnSelector
              options={columnOptions}
              onChange={(v) => onAddFormulaOrColumn(v)}
              dropdownClassName={isNarrowScreen && '-top-[250px]'}
            />
            <ColumnSelector
              options={functionOptions}
              onChange={(v) => onAddFormulaOrColumn(v)}
              placeholder='Functions'
              label=''
              dropdownClassName={isNarrowScreen && '-top-[250px]'}
            />
          </div>
          <div className={clsx('w-full', isAIFormulaMode && !formula && 'hidden')}>
            <Tooltip
              text={tooltipContentCondition}
              side='left'
              className='!normal-case !opacity-100 isFormulasInputFocused!text-[13px] !leading-[17px]'
              tooltipTriggerClassName='w-full'
              open={isFormulasInputFocused}
            >
              <div className='relative'>
                <div
                  className={clsx(
                    'absolute w-full pointer-events-none overflow-hidden',
                    textareaHeight
                  )}
                >
                  <span
                    data-cy='formula-markup'
                    ref={fakeTextareaRef}
                    className={clsx(
                      'text-left absolute w-full pointer-events-none text-sm p-[9px] top-0 left-0 break-words',
                      textareaHeight
                    )}
                    dangerouslySetInnerHTML={{ __html: convertedFormula }}
                    style={{
                      top: textAreaTopScroll,
                      paddingRight: textAreaScrollbarWidth,
                    }}
                  ></span>
                </div>

                <Textarea
                  ref={textareaRef}
                  value={formula}
                  onChange={onTextareaChange}
                  className={clsx(
                    'text-white resize-none caret-black active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100 rounded-t-none',
                    textareaHeight
                  )}
                  onKeyDown={onKeyDown}
                  placeholder={getTextareaPlaceholder()}
                  disabled={isLoading}
                  onFocus={onTextareaFocus}
                  onBlur={onTextareaBlur}
                  onKeyUp={onTextareaKeyUp}
                  onMouseUp={onTextareaMouseUp}
                  onScroll={onTextareaScroll}
                  dataCy='formula-textarea'
                />
                {autocompleteContent()}
              </div>
            </Tooltip>
          </div>

          <div
            className={clsx(
              'flex w-full mt-1 mb-2 justify-between items-start',
              isAIFormulaMode && !formula && 'hidden'
            )}
          >
            <div>{textareaHelperText()}</div>

            {!isSnowflakeFile && (
              <div className='flex items-center justify-end'>
                <Button
                  variant='outline'
                  color='oceanBlue'
                  size='small'
                  onClick={() => handleGeneratePreview()}
                  onKeyDown={handleKeyDownGeneratePreview()}
                  disabled={!formula || isLoading}
                >
                  Generate Preview
                </Button>
              </div>
            )}
          </div>

          {!!formulaPreview.length && (
            <FormulaPreviewContainer
              preview={formulaPreview}
              showFormulaBuilderFeedback={showFormulaBuilderFeedback}
              setShowFormulaBuilderFeedback={setShowFormulaBuilderFeedback}
              aiFormulaQuery={aiFormulaQuery}
              setAiFormulaQuery={setAiFormulaQuery}
              aiFormulaResponse={aiFormulaResponse}
            />
          )}
        </div>
      )}

      <div className='flex flex-row items-center justify-between mt-6 text-right'>
        {currentModalOption === 'formula' && (
          <div className='flex justify-end'>
            <Button
              variant='ghost'
              color='capri'
              iconName='question'
              className='!ring-0 outline-none'
              onClick={onHelpButtonClick}
            >
              Help
            </Button>
          </div>
        )}

        {formulaToEdit ? (
          <div>
            {currentModalOption === 'formula' && (
              <Button
                color='capri'
                variant='ghost'
                iconName='arrow-clockwise'
                dataCy='refresh-calculations-btn'
                disabled={isAddButtonDisabled()}
                onClick={handleApplyFormula}
              >
                Refresh Calculations
              </Button>
            )}
          </div>
        ) : (
          <div></div>
        )}
        <div className='flex gap-3'>
          <Button variant='ghost' color='shadow' dataCy='cancel-btn' onClick={closeModal}>
            Cancel
          </Button>
          <Button
            color={'oceanBlue'}
            onClick={() => {
              if (user?.is_anonymous) {
                closeModal();
                dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
                return;
              }
              currentModalOption === 'iterator' ? onAddIteratorColumn() : handleApplyFormula();
            }}
            disabled={isAddButtonDisabled()}
            className='shrink-0'
            dataCy='submit-btn'
          >
            {formulaToEdit ? 'Edit Formula' : 'Add Column'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormulasPopover;
